/**
 * Mail drafts are edited as Markdown (Milkdown). A `mailto:` link carries plain
 * text only; the clipboard carries both formats, so pasting into a mail client
 * keeps headings, lists and tables. Pure, no I/O.
 */
import { micromark } from 'micromark';
import { gfm, gfmHtml } from 'micromark-extension-gfm';

/**
 * Markdown as mail HTML. Raw HTML in the text is escaped (micromark's default),
 * guidance comments are dropped, and tables get inline borders because many
 * mail clients ignore style sheets.
 */
export function markdownToHtml(markdown: string): string {
  const body = micromark(markdown.replace(/<!--[\s\S]*?-->/g, ''), {
    extensions: [gfm()],
    htmlExtensions: [gfmHtml()],
  })
    .replace(/<table>/g, '<table border="1" cellpadding="6" style="border-collapse:collapse">');
  return `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.5">${body}</div>`;
}

/** Plain text of a Markdown draft — what a `mailto:` link can carry. */
export function markdownToPlainText(markdown: string): string {
  const lines = markdown
    .replace(/\r\n?/g, '\n')
    .replace(/<!--[\s\S]*?-->/g, '')
    .split('\n')
    .map((line) => line
      // Headings keep their text.
      .replace(/^ {0,3}#{1,6}\s+/, '')
      // Bullet lists read as bullets.
      .replace(/^(\s*)[-*+]\s+/, '$1• ')
      // Links: "Text (https://…)".
      .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '$1 ($2)')
      // Emphasis and inline code markers.
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      .replace(/(^|[^*\w])\*([^*\n]+)\*(?=[^*\w]|$)/g, '$1$2')
      .replace(/`([^`]+)`/g, '$1')
      // Milkdown escapes punctuation (e.g. {{fixed\_version}}).
      .replace(/\\([\\`*_{}[\]()#+\-.!|>~])/g, '$1'));
  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}
